/**
 * Núcleo puro da Ficha do mês (`/equipe/ficha/:funcionarioId`) — sem banco,
 * sem Express. A Ficha existe para responder "quanto essa pessoa recebe este
 * mês, e por quê" num lugar só; hoje essa conta está espalhada (card da
 * quinzena, rodapé "Sai em", Simulação da Remuneração, fechamento mensal),
 * cada um com uma fatia.
 *
 * ⚠️ A Ficha NÃO recalcula nada que já existe — fixo/variável vêm de
 * `getSimulacao` (Remuneração), adiantamentos/descontos vêm de
 * `fechamento_mensal`. O que este arquivo faz é a única coisa que hoje não
 * existe em lugar nenhum: SOMAR essas parcelas (de fontes diferentes) numa
 * conta única, e ler presença de `tarefas`. Não é uma segunda fórmula de
 * pagamento — é o primeiro lugar que junta o que já foi calculado.
 */

import { paraCentavos, paraReais } from "./equipe-fechamento.js";

// ─── Presença ───────────────────────────────────────────────────────────────

export interface TarefaPresenca {
  funcionarioId: string;
  data: string; // 'YYYY-MM-DD'
  status: string;
}

export interface PresencaDoMes {
  diasTrabalhados: number;
  diasFalta: number;
  /** Datas distintas de falta, ordenadas — para o "(03/08, 17/08)" da tela. */
  datasFalta: string[];
}

/**
 * Presença do mês a partir de `tarefas` — dias distintos com `CONCLUIDA`
 * (trabalhados) e com `FALTOU` (faltas), mesmo critério do fechamento
 * (`COUNT(DISTINCT data)`). Funciona igual para diarista e mensalista: os
 * dois têm tarefa por trás do dia.
 */
export function presencaDoMes(tarefas: TarefaPresenca[], funcionarioId: string): PresencaDoMes {
  const minhas = tarefas.filter(t => t.funcionarioId === funcionarioId);
  const trabalhados = new Set(minhas.filter(t => t.status === "CONCLUIDA").map(t => t.data));
  const faltas = new Set(minhas.filter(t => t.status === "FALTOU").map(t => t.data));
  return {
    diasTrabalhados: trabalhados.size,
    diasFalta: faltas.size,
    datasFalta: Array.from(faltas).sort(),
  };
}

/** "R$ 130,56" — fixo ÷ dias trabalhados. `null` sem dia trabalhado (divisão sem sentido, não 0). */
export function calcCustoPorDia(fixoCentavos: number, diasTrabalhados: number): string | null {
  if (diasTrabalhados <= 0) return null;
  return paraReais(Math.round(fixoCentavos / diasTrabalhados));
}

// ─── A conta inteira — o que não tem dono hoje ─────────────────────────────

export interface BrutoFichaInput {
  fixoCentavos: number;
  variavelCentavos: number;
  auxiliosCentavos: number;
  adicionaisCentavos: number;
}

/** bruto = fixo + variável + auxílios + adicionais — a PRIMEIRA vez que as quatro parcelas se somam num lugar só. */
export function calcBrutoFichaCentavos(args: BrutoFichaInput): number {
  return args.fixoCentavos + args.variavelCentavos + args.auxiliosCentavos + args.adicionaisCentavos;
}

/** líquido = bruto − adiantamentos pagos − descontos. `null` quando falta uma das duas (sem fechamento gerado ainda). */
export function calcLiquidoFichaCentavos(args: {
  brutoCentavos: number;
  adiantamentosPagos: string | number | null | undefined;
  descontos: string | number | null | undefined;
}): number {
  return args.brutoCentavos - paraCentavos(args.adiantamentosPagos) - paraCentavos(args.descontos);
}
